// SyncProgressStrip — подпись + полоска ожидания данных: баннером под шапкой
// (первичный синк) и плавающей карточкой поверх скелетона страницы (всё
// остальное ожидание).
// Детерминированный % — только если SDK отдал downloadProgress; иначе
// indeterminate, чтобы пользователь видел, что загрузка живая, а не зависла.
//
// Вариант `overlay` не занимает места в потоке: раньше подпись стояла блоком
// НАД скелетоном и сдвигала его вниз, а с приходом данных исчезала — контент
// прыгал на её высоту (CLS). Позиционирует карточку потребитель, здесь только
// её вид.
import { Progress } from "@studyhub/ui/components/progress";
import { IconCloudDownload, IconCloudUp, IconLoader2 } from "@tabler/icons-react";
import type { PowerSyncPhase } from "@/hooks/use-powersync-status";

interface SyncProgressStripProps {
	label: string;
	phase: PowerSyncPhase;
	/** `null` — длительность неизвестна, полоска идёт indeterminate. */
	percent: number | null;
	/** Пояснение под полоской, когда ожидание затянулось. */
	hint?: string | undefined;
	/** `banner` — полная ширина под топбаром; `overlay` — карточка поверх скелетона. */
	variant?: "banner" | "overlay";
}

/**
 * Облако — только когда речь действительно про синк. На обычном ожидании
 * данных (база уже скачана) облако обещало бы загрузку библиотеки, которой
 * нет, поэтому там нейтральный спиннер.
 */
function StatusIcon({ phase }: { phase: PowerSyncPhase }): React.ReactElement {
	if (phase === "downloading") {
		return (
			<IconCloudDownload
				size={16}
				stroke={1.75}
				className="animate-pulse text-primary"
				aria-hidden
			/>
		);
	}
	if (phase === "connecting" || phase === "initializing") {
		return (
			<IconCloudUp size={16} stroke={1.75} className="animate-pulse text-primary" aria-hidden />
		);
	}
	return <IconLoader2 size={16} stroke={1.75} className="animate-spin text-primary" aria-hidden />;
}

export function SyncProgressStrip({
	label,
	phase,
	percent,
	hint,
	variant = "banner",
}: SyncProgressStripProps): React.ReactElement {
	const banner = variant === "banner";

	// Фон баннера обязан быть НЕПРОЗРАЧНЫМ: стопка баннеров `fixed` под шапкой,
	// и контент страницы при прокрутке уходит под неё. Прежний `bg-primary/5`
	// (5% синего поверх прозрачного) пропускал текст насквозь — на iOS заголовок
	// мастера курса читался прямо поверх «Загрузка библиотеки…». Тот же оттенок,
	// но смешанный с фоном страницы, а не с пустотой.
	return (
		<div
			role="status"
			aria-live="polite"
			data-testid="sync-progress-strip"
			className={
				banner
					? "flex flex-col items-center gap-1 border-primary/20 border-b bg-[color-mix(in_oklab,var(--color-primary)_5%,var(--color-background))] px-4 py-2 text-foreground text-sm"
					: "flex w-[min(19rem,calc(100vw-3rem))] animate-drop-in flex-col items-center gap-2 rounded-[var(--radius-card)] border border-border bg-card/90 px-5 py-4 text-foreground text-sm shadow-e2 backdrop-blur-xl backdrop-saturate-150"
			}
		>
			<div className="flex items-center gap-2">
				<StatusIcon phase={phase} />
				<span className="tabular-nums">{label}</span>
			</div>
			<Progress value={percent} className="h-1 w-full max-w-xs" />
			{hint ? <span className="text-center text-muted-foreground text-xs">{hint}</span> : null}
		</div>
	);
}
